import { useEffect, useMemo, useState, useRef } from "react"
import { isEqual, omit } from "lodash"
import { makeFindAndGetBaseMetadata } from "helpers/base"
import { ERROR, OK } from "helpers/data-statuses"
import type { PreferencesValidationOptions, PreferencesValidationResult } from "hooks/use-preferences/types"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useSaveRecentInstrument } from "hooks/use-save-recent-instrument"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { useActiveInstruments, RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { DASHBOARD_EVENTS, useDashboardAnalytics } from "../../analytics"
import { useDashboardId } from "../../context/dashboard-id"
import { coreSlice } from "../../store/core-slice"
import { useGetUrlInstrumentParams, type UrlInstrument } from "../use-get-instrument-url-params"
import { resolveUrlInstrumentOverride } from "./resolve-url-instrument-override"
import { validateDashboardPreferences } from "./validate-dashboard-preferences"

const EXPERIMENT_NOT_FOUND_MESSAGE = "The experiment in this link couldn't be found. Showing the portfolio instead."

function getUrlInstrumentKey(urlInstrument: UrlInstrument | undefined) {
  const { urlInstrumentType, urlInstrumentId, urlExperimentId } = urlInstrument ?? {}
  if (!urlInstrumentType || !urlInstrumentId) {
    return null
  }
  return `${urlInstrumentType}:${urlInstrumentId}${urlExperimentId ? `:${urlExperimentId}` : ""}`
}

export function useDashboardPreferencesValidation(
  modelId: string | null,
  { isStateAuthoritativeAtMount = false }: { isStateAuthoritativeAtMount?: boolean } = {},
) {
  const dashboardId = useDashboardId()
  const dispatch = useAppDispatch()
  const analytics = useDashboardAnalytics()
  const { saveRecentInstrument } = useSaveRecentInstrument(modelId)
  const [activeInstruments, activeInstrumentsStatus] = useActiveInstruments(modelId, true)
  const [preferenceValidationOptions, setPreferenceValidationOptions] = useState<PreferencesValidationOptions | null>(null)
  const { instrument: urlInstrument, clearInstrumentFromUrl } = useGetUrlInstrumentParams()
  const currentInstrument = useAppSelector((state) => state.dashboard.core[dashboardId]?.instrument)

  const urlInstrumentKey = getUrlInstrumentKey(urlInstrument)
  const lastValidatedResultRef = useRef<PreferencesValidationResult | null>(null)
  const dispatchedUrlInstrumentKeyRef = useRef<string | null>(null)
  const validatedUrlInstrumentKeyRef = useRef<string | null>(null)
  const experimentLinkHandledKeyRef = useRef<string | null>(null)
  // An authoritative-at-mount slot (instance-mode seed) makes usePreferences skip its validate
  // pass, so arm the override effect from mount — else it waits forever for a run that never
  // happens. [APP-3328]
  const hasCompletedInitialValidationRef = useRef(isStateAuthoritativeAtMount)

  const instrumentsSettled = activeInstrumentsStatus === OK || activeInstrumentsStatus === ERROR

  const urlInstrumentOverride = useMemo(() => {
    if (!urlInstrumentKey || !activeInstruments || activeInstrumentsStatus !== OK) {
      return null
    }
    return resolveUrlInstrumentOverride(
      urlInstrument,
      activeInstruments,
      makeFindAndGetBaseMetadata(null, activeInstruments),
    )
  }, [urlInstrumentKey, urlInstrument, activeInstruments, activeInstrumentsStatus])

  useEffect(() => {
    if (!hasCompletedInitialValidationRef.current || !urlInstrumentKey) {
      return
    }
    if (urlInstrumentKey === dispatchedUrlInstrumentKeyRef.current) {
      return
    }
    if (!modelId || !urlInstrumentOverride) {
      return
    }

    dispatchedUrlInstrumentKeyRef.current = urlInstrumentKey
    dispatch(coreSlice.actions.setInstrument({ dashboardId, instrument: urlInstrumentOverride }))
  }, [
    urlInstrumentKey,
    modelId,
    urlInstrumentOverride,
    dashboardId,
    dispatch,
  ])

  useEffect(() => {
    if (
      (!isStateAuthoritativeAtMount && !preferenceValidationOptions)
      || urlInstrument?.urlInstrumentType !== "portfolio"
      || !urlInstrument?.urlExperimentId
      || !urlInstrumentKey
      || !modelId
      || !activeInstruments
      || !urlInstrumentOverride
      || experimentLinkHandledKeyRef.current === urlInstrumentKey
    ) {
      return
    }

    if (urlInstrumentOverride.experimentId) {
      experimentLinkHandledKeyRef.current = urlInstrumentKey
      const experimentName = activeInstruments.experiments
        ?.find((experiment) => experiment.id === urlInstrumentOverride.experimentId)?.name
      analytics.track(DASHBOARD_EVENTS.INITIAL_INSTRUMENT_SELECTION, {
        instrument: { id: urlInstrumentOverride.experimentId, name: experimentName, type: "experiment" },
        searchType: RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD,
      })
      saveRecentInstrument(
        { instrumentType: "experiment", instrumentId: urlInstrumentOverride.experimentId },
        RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD,
      )
    } else {
      experimentLinkHandledKeyRef.current = urlInstrumentKey
      dispatch(NotificationCreators.add("warning", null, EXPERIMENT_NOT_FOUND_MESSAGE))
    }
  }, [
    isStateAuthoritativeAtMount,
    preferenceValidationOptions,
    urlInstrument,
    urlInstrumentKey,
    urlInstrumentOverride,
    modelId,
    activeInstruments,
    analytics,
    saveRecentInstrument,
    dispatch,
  ])

  useEffect(() => {
    // Without a model, instrument validation is impossible. Fire the callback
    // immediately so the dashboard preferences loading gate is not blocked while
    // model preferences are still in-flight. Validation will re-run once modelId
    // arrives and instruments finish loading.
    const isReady = !modelId || activeInstrumentsStatus === OK || activeInstrumentsStatus === ERROR
    if (!preferenceValidationOptions || !isReady) {
      return
    }

    const { preferences, callback } = preferenceValidationOptions

    const preferencesForDashboard = typeof preferences.modelId === "string"
      ? omit(preferences, "modelId")
      : preferences

    let validationResult = {
      isCorrected: false,
      preferences: preferencesForDashboard,
    }

    if (modelId && activeInstrumentsStatus === OK && activeInstruments) {
      const shouldApplyUrlOverride = Boolean(
        urlInstrumentKey && urlInstrumentKey !== validatedUrlInstrumentKeyRef.current,
      )
      let preferencesInput = preferencesForDashboard

      if (
        !shouldApplyUrlOverride
        && urlInstrumentKey
        && urlInstrumentKey === validatedUrlInstrumentKeyRef.current
        && currentInstrument
      ) {
        preferencesInput = { ...preferencesForDashboard, instrument: currentInstrument }
      }

      validationResult = validateDashboardPreferences(
        preferencesInput,
        activeInstruments,
        shouldApplyUrlOverride ? { urlInstrument } : {},
      )

      if (shouldApplyUrlOverride) {
        if (urlInstrumentOverride) {
          lastValidatedResultRef.current = null
          if (isEqual(validationResult.preferences.instrument, urlInstrumentOverride)) {
            validatedUrlInstrumentKeyRef.current = urlInstrumentKey
          }
        } else {
          validatedUrlInstrumentKeyRef.current = urlInstrumentKey
        }
      }
    }

    if (!isEqual(validationResult, lastValidatedResultRef.current)) {
      callback(validationResult)
    }
    lastValidatedResultRef.current = validationResult
    hasCompletedInitialValidationRef.current = true
  }, [
    activeInstrumentsStatus,
    preferenceValidationOptions,
    activeInstruments,
    modelId,
    urlInstrument,
    urlInstrumentKey,
    urlInstrumentOverride,
    currentInstrument,
  ])

  // Clear the params only once a dashboard that can consume them has mounted (model + instruments
  // settled). Bare /dashboard resolves to the model-less "default" sentinel for a beat, then
  // remounts under the real instance (new key → fresh state); clearing during that throwaway mount
  // would drop the deep link before the instance sees it. Declared last so the override is read
  // before the clear within a commit. [APP-3328]
  useEffect(() => {
    if (urlInstrumentKey && modelId && instrumentsSettled) {
      clearInstrumentFromUrl()
    }
  }, [urlInstrumentKey, modelId, instrumentsSettled, clearInstrumentFromUrl])

  return setPreferenceValidationOptions
}
